import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HUMAN = (k) => k.replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase());

const inputTypeFor = (k) => {
  if (k === 'dob' || k === 'anniversary_date') return 'date';
  if (k === 'height' || k === 'weight') return 'number';
  return 'text';
};

/**
 * Public shareable form — no auth required. Loads its definition by slug
 * and submits back to the same slug endpoint.
 */
const PublicForm = () => {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [values, setValues] = useState({ name: '', phone: '', email: '' });
  const [extra, setExtra] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/lead-forms/${slug}`)
      .then(r => setForm(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Form not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    const phoneDigits = (values.phone || '').replace(/\D/g, '');
    if (!values.name.trim()) return alert('Please enter your name');
    if (phoneDigits.length !== 10) return alert('Please enter a valid 10-digit phone number');
    setSending(true);
    try {
      await axios.post(`${API}/public/lead-forms/${slug}/submit`, {
        name: values.name.trim(),
        phone: phoneDigits,
        email: values.email || null,
        extra,
        consent: true,
      });
      setDone(true);
    } catch (e2) {
      alert(e2.response?.data?.detail || 'Submission failed. Please try again.');
    } finally { setSending(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="spinner" /></div>;
  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="text-center max-w-md">
        <p className="text-lg font-semibold text-slate-800">{err}</p>
        <p className="text-sm text-slate-500 mt-2">This link may have expired or is no longer active.</p>
      </div>
    </div>
  );

  if (done) return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-blue-50 p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="public-form-thanks">
        <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Thank you!</h2>
        <p className="text-slate-600 mt-2 whitespace-pre-line">{form?.thank_you_message}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4" data-testid="public-form-page">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="bg-blue-800 text-white px-6 py-6">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>{form.header_title || form.name}</h1>
          {form.description && <p className="text-blue-100 mt-1 text-sm">{form.description}</p>}
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
            <input
              type="text" required value={values.name}
              onChange={(e) => setValues({ ...values, name: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800"
              data-testid="public-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone <span className="text-red-500">*</span></label>
            <input
              type="tel" required inputMode="numeric" maxLength={10}
              value={values.phone}
              onChange={(e) => setValues({ ...values, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800"
              placeholder="10-digit mobile number"
              data-testid="public-phone-input"
            />
          </div>
          {(form.enabled_fields || []).includes('email') && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email" value={values.email}
                onChange={(e) => setValues({ ...values, email: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800"
                data-testid="public-email-input"
              />
            </div>
          )}
          {(form.enabled_fields || []).filter(f => f !== 'email').map(f => (
            <div key={f}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{HUMAN(f)}</label>
              <input
                type={inputTypeFor(f)}
                value={extra[f] || ''}
                onChange={(e) => setExtra({ ...extra, [f]: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-800"
                data-testid={`public-extra-${f}`}
              />
            </div>
          ))}
          <button
            type="submit" disabled={sending}
            className="w-full flex items-center justify-center gap-2 bg-blue-800 hover:bg-blue-900 disabled:bg-slate-400 text-white font-medium py-2.5 rounded-md transition-colors"
            data-testid="public-submit-button"
          >
            <Send className="h-4 w-4" />
            {sending ? 'Submitting…' : 'Submit'}
          </button>
          <p className="text-[10px] text-slate-400 text-center">Your information is only shared with the agent who created this form.</p>
        </form>
      </div>
    </div>
  );
};

export default PublicForm;
